import React, { useEffect, useMemo, useState } from "react";
import { MediaRequests } from "@app/shared/src/api/api.gen";
import { getApiUrl } from "@app/shared/src/api/api_client";
import { DeviceMediaRequest, IDeviceMediaRequest } from "@app/shared/src/models";
import {
    Btn,
    Card,
    Combo,
    Modal,
    Note,
    OptionButton,
    Select,
    Spinner,
    Table,
    TableBar,
    Td,
    Th,
    formatDateTime,
    formatTimeOfDay,
    timeZoneLabel,
    useToast,
} from "@app/shared/src/components_v2";
import { siteLabel } from "../../helpers/deviceLabels";
import { mediaRequestPhase } from "../../helpers/mediaRequestStatus";
import { useLoad } from "../../hooks/useLoad";
import { urlEnum, urlOptionalId, useUrlState, UrlCodec } from "../../hooks/useUrlState";
import { useFleet } from "../providers/FleetProvider";
import { MediaRequestStatusTag } from "../shared/MediaRequestStatusTag";
import { PageTitle } from "../shared/PageTitle";

/**
 * Every DVR pull across the fleet, in one place.
 *
 * The server only lists media requests per device, so this page fans out one request per unit and
 * merges — fine at fleet scale, and it keeps "what footage have we asked the trucks for?" a single
 * screen instead of a per-device scavenger hunt.
 */

const WINDOWS = [
    { label: "Last 2 min", minutes: 2 },
    { label: "Last 10 min", minutes: 10 },
];
/** Longer than this and the unit is uploading over LTE for a very long time. */
const MAX_CUSTOM_MINUTES = 60;
const QUALITIES = [
    { label: "Standard", width: 640, fps: 15 },
    { label: "High", width: 1280, fps: 30 },
];

type Row = DeviceMediaRequest & { _deviceId: string };

/**
 * A Date as `<input type="datetime-local">` wants it: local wall-clock, no zone suffix.
 * toISOString would shift the displayed time by the UTC offset, which reads as the picker
 * silently rewriting what you typed.
 */
function toLocalInputValue(d: Date): string {
    const pad = (n: number) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** Elapsed clip length, from the requested window. */
function windowLength(r: Row): string {
    if (!r.periodStart || !r.periodEnd) return "—";
    const seconds = Math.round((new Date(r.periodEnd).getTime() - new Date(r.periodStart).getTime()) / 1000);
    if (seconds < 60) return `${seconds} s`;
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return s === 0 ? `${m} min` : `${m} min ${s} s`;
}

/**
 * A queued pull lands in a couple of minutes, so the table watches for it rather than making
 * someone reload to see "Uploading 60%" move. Each tick is one request per unit — the fan-out this
 * page is built on — so the cost scales with the fleet; usePoll at least stops it on a hidden tab.
 */
const REFRESH_MS = 30_000;

/**
 * Newest pulls asked of each unit per tick. The table shows the newest 100 across the fleet, so
 * more than that per unit can never reach the screen — and the unpaged list endpoint would hand
 * back every request a unit has ever had, which on a busy sensor runs past the client's timeout.
 * The status filter therefore applies to each unit's newest 100, not to its whole history.
 */
const PER_UNIT_LIMIT = 100;

type StatusKey = "all" | "ready" | "working" | "failed";
const STATUS_CODEC = urlEnum<StatusKey>(["all", "ready", "working", "failed"]);

/**
 * The unit picker's "all" is the absence of a filter, so it is the default and never reaches the
 * URL — `?device=all` and no parameter at all would otherwise be two spellings of one view.
 */
const UNIT_CODEC: UrlCodec<string> = {
    parse: (raw) => ({ value: raw || "all" }),
    format: (value) => urlOptionalId.format(value === "all" ? undefined : value),
};

export const MediaPage: React.FC = () => {
    // Trucks only — a clip request against a gantry counter is not something to offer.
    const { statsDevices: devices, deviceSite, deviceKey, group, isLoading: fleetLoading } = useFleet();
    const toast = useToast();
    const [unit, setUnit] = useUrlState<string>("device", "all", UNIT_CODEC);
    const [status, setStatus] = useUrlState<StatusKey>("status", "all", STATUS_CODEC);
    const [modalOpen, setModalOpen] = useState(false);

    // A unit picked before the group changed has dropped out of scope. Clearing it beats leaving
    // the table filtered by a truck that is no longer in the picker to unpick.
    useEffect(() => {
        if (unit !== "all" && devices.length > 0 && !devices.some((d) => d.id === unit)) {
            setUnit("all");
        }
    }, [unit, devices, setUnit]);

    const requests = useLoad<Row[]>(async () => {
        const perDevice = await Promise.all(
            devices.map(async (d) => {
                try {
                    const page = await MediaRequests.getRequestsInfiniteScroll(d.id, PER_UNIT_LIMIT);
                    return (page?.data ?? []).map((r) => Object.assign(r, { _deviceId: d.id! }) as Row);
                } catch {
                    return [] as Row[];
                }
            }),
        );
        return perDevice
            .flat()
            .sort((a, b) => new Date(b.requestDate ?? 0).getTime() - new Date(a.requestDate ?? 0).getTime());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [devices.map((d) => d.id).join(",")], { pollMs: REFRESH_MS });

    const rows = useMemo(() => {
        return (requests.data ?? []).filter((r) => {
            if (unit !== "all" && r._deviceId !== unit) return false;
            return status === "all" || mediaRequestPhase(r) === status;
        });
    }, [requests.data, unit, status]);

    const viewUrl = (r: Row) => getApiUrl(`/api/mediarequest/${r._deviceId}/media-requests/${r.id}/video/view`);

    const cancel = async (r: Row) => {
        try {
            await MediaRequests.deleteRequest(r._deviceId, r.id);
            toast("Request cancelled");
            requests.reload();
        } catch (err) {
            toast(err instanceof Error ? err.message : "Could not cancel that");
        }
    };

    return (
        <div className="flex flex-col">
            <TableBar title={<PageTitle>DVR requests</PageTitle>}>
                <Combo
                    value={unit}
                    onChange={setUnit}
                    placeholder="Search TMA or device key…"
                    options={[
                        { value: "all", label: "All units" },
                        ...devices.map((d) => ({ value: d.id!, label: siteLabel(d), sub: d.deviceKey })),
                    ]}
                />
                <Select value={status} onChange={(e) => setStatus(e.target.value as typeof status)}>
                    <option value="all">All statuses</option>
                    <option value="ready">Ready</option>
                    <option value="working">Working</option>
                    <option value="failed">Failed</option>
                </Select>
                <Btn onClick={() => setModalOpen(true)}>New request</Btn>
            </TableBar>

            {requests.error && <Note tone="crit">{requests.error}</Note>}

            <Card>
                {fleetLoading || requests.isLoading ? (
                    <div className="grid place-items-center p-10 text-[var(--muted)]">
                        <Spinner />
                    </div>
                ) : rows.length === 0 ? (
                    <p className="px-5 py-6 text-[13px] text-[var(--muted)]">
                        {unit !== "all" || status !== "all"
                            ? "Nothing matches those filters."
                            : group
                              ? `No clips have been pulled from ${group.name} yet.`
                              : "No clips have been pulled from the fleet yet."}
                    </p>
                ) : (
                    <Table>
                            <thead>
                                <tr>
                                    <Th>Requested ({timeZoneLabel()})</Th>
                                    <Th>TMA</Th>
                                    <Th>Window</Th>
                                    <Th>Length</Th>
                                    <Th>Status</Th>
                                    <Th className="text-right" />
                                </tr>
                            </thead>
                            <tbody>
                                {rows.slice(0, 100).map((r) => {
                                    const phase = mediaRequestPhase(r);
                                    return (
                                        <tr key={r.id} className="hover:bg-[var(--surface-2)]">
                                            <Td label="Requested" className="tabular-nums">{formatDateTime(r.requestDate)}</Td>
                                            {/* The TMA number is what a director asks for footage by; the
                                                key underneath settles which box is bolted to that truck. */}
                                            <Td label="TMA">
                                                <span className="block font-semibold">{deviceSite(r._deviceId)}</span>
                                                <span className="block font-mono text-[12px] text-[var(--muted)]">
                                                    {deviceKey(r._deviceId) ?? "—"}
                                                </span>
                                            </Td>
                                            <Td label="Window" className="tabular-nums text-[var(--muted)]">
                                                {formatTimeOfDay(r.periodStart)} – {formatTimeOfDay(r.periodEnd)}
                                            </Td>
                                            <Td label="Length" className="tabular-nums">{windowLength(r)}</Td>
                                            <Td label="Status">
                                                <MediaRequestStatusTag request={r} />
                                            </Td>
                                            <Td className="text-right">
                                                {phase === "ready" ? (
                                                    <a href={viewUrl(r)} target="_blank" rel="noreferrer">
                                                        <Btn tone="quiet">Watch</Btn>
                                                    </a>
                                                ) : phase === "working" ? (
                                                    <Btn tone="quiet" onClick={() => cancel(r)}>
                                                        Cancel
                                                    </Btn>
                                                ) : null}
                                            </Td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                    </Table>
                )}
            </Card>

            <NewRequestModal
                open={modalOpen}
                onClose={() => setModalOpen(false)}
                defaultDeviceId={unit !== "all" ? unit : devices[0]?.id}
                onDone={() => {
                    setModalOpen(false);
                    requests.reload();
                }}
            />
        </div>
    );
};

export const NewRequestModal: React.FC<{
    open: boolean;
    onClose: () => void;
    defaultDeviceId?: string;
    onDone: () => void;
}> = ({ open, onClose, defaultDeviceId, onDone }) => {
    const { statsDevices: devices, allDevices } = useFleet();
    const toast = useToast();
    const [deviceId, setDeviceId] = useState<string>();
    const [minutes, setMinutes] = useState(10);
    const [quality, setQuality] = useState(QUALITIES[0]);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string>();

    /*
     * "Just now" is the common case, but not the only one: a director reviewing an incident from
     * this morning needs the footage from this morning, and walking a fixed window backwards can't
     * get there. Custom mode asks for a start and a length instead.
     */
    const [custom, setCustom] = useState(false);
    const [startLocal, setStartLocal] = useState("");
    const [customMinutes, setCustomMinutes] = useState(10);

    const effectiveDevice = deviceId ?? defaultDeviceId ?? devices[0]?.id;

    // The picker offers the fleet, but the modal can be opened from any unit's own page — one
    // outside the fleet list included — and that unit must still read by name, not by GUID.
    const options = useMemo(() => {
        const fleet = devices.map((d) => ({ value: d.id!, label: siteLabel(d), sub: d.deviceKey }));
        if (!effectiveDevice || fleet.some((o) => o.value === effectiveDevice)) return fleet;
        const extra = allDevices.find((d) => d.id === effectiveDevice);
        return extra ? [{ value: extra.id!, label: siteLabel(extra), sub: extra.deviceKey }, ...fleet] : fleet;
    }, [devices, allDevices, effectiveDevice]);

    // What the unit still has on disk. Asking outside it returns an empty clip with no
    // explanation, so the window is stated up front and the request is blocked if it falls out.
    const dvrRange = useLoad(
        () => (effectiveDevice && open ? MediaRequests.getEarliestVideo(effectiveDevice) : Promise.resolve(undefined)),
        [effectiveDevice, open],
    );

    // Seed the picker with "an hour ago" the first time custom is opened — a real starting point
    // beats an empty field, and datetime-local wants local wall-clock, not an ISO instant.
    useEffect(() => {
        if (!custom || startLocal) return;
        const seed = new Date(Date.now() - 3_600_000);
        seed.setSeconds(0, 0);
        setStartLocal(toLocalInputValue(seed));
    }, [custom, startLocal]);

    const range = useMemo(() => {
        if (!custom) {
            const end = new Date();
            return { start: new Date(end.getTime() - minutes * 60_000), end };
        }
        if (!startLocal) return undefined;
        const start = new Date(startLocal);
        if (Number.isNaN(start.getTime())) return undefined;
        return { start, end: new Date(start.getTime() + customMinutes * 60_000) };
    }, [custom, minutes, startLocal, customMinutes]);

    const rangeProblem = useMemo(() => {
        if (!range) return "Pick a start time.";
        if (range.end.getTime() > Date.now() + 60_000) return "That window runs into the future.";
        if (customMinutes < 1 || customMinutes > MAX_CUSTOM_MINUTES) return `Length must be between 1 and ${MAX_CUSTOM_MINUTES} minutes.`;
        const earliest = dvrRange.data?.start ? new Date(dvrRange.data.start) : undefined;
        if (earliest && range.start < earliest) {
            return `This unit's recording only goes back to ${formatDateTime(earliest)}.`;
        }
        return undefined;
    }, [range, customMinutes, dvrRange.data]);

    const send = async () => {
        if (!effectiveDevice || busy || !range || (custom && rangeProblem)) return;
        setBusy(true);
        setError(undefined);
        try {
            const { start, end } = range;
            await MediaRequests.createRequest(
                effectiveDevice,
                new DeviceMediaRequest({
                    deviceId: effectiveDevice,
                    periodStart: start,
                    periodEnd: end,
                    includeVideo: true,
                    includeRadarData: false,
                    desiredWidth: quality.width,
                    desiredFps: quality.fps,
                    // Partial by intent: the server fills id, status, dates. The generated interface
                    // declares every server-side field, hence the unknown hop.
                } as unknown as IDeviceMediaRequest),
            );
            toast(`Requested from ${siteLabel(allDevices.find((d) => d.id === effectiveDevice))}`);
            onDone();
        } catch (err) {
            setError(err instanceof Error ? err.message : "Could not request that clip");
        } finally {
            setBusy(false);
        }
    };

    return (
        <Modal
            open={open}
            onClose={onClose}
            title="Request a clip"
            subtitle="Pulled from the device over LTE — a short clip usually lands in a couple of minutes"
            footer={
                <>
                    <Btn tone="quiet" onClick={onClose}>
                        Cancel
                    </Btn>
                    <Btn onClick={send} disabled={busy || !effectiveDevice || (custom && Boolean(rangeProblem))}>
                        {busy ? "Sending…" : "Send request"}
                    </Btn>
                </>
            }
        >
            <label className="text-[12px] text-[var(--muted)]">TMA</label>
            <Combo
                value={effectiveDevice ?? ""}
                onChange={setDeviceId}
                placeholder="Search TMA or device key…"
                options={options}
            />
            <label className="text-[12px] text-[var(--muted)]">Window</label>
            <div className="flex flex-wrap gap-2">
                {WINDOWS.map((w) => (
                    <OptionButton
                        key={w.minutes}
                        selected={!custom && minutes === w.minutes}
                        onClick={() => {
                            setCustom(false);
                            setMinutes(w.minutes);
                        }}
                    >
                        {w.label}
                    </OptionButton>
                ))}
                <OptionButton selected={custom} onClick={() => setCustom(true)}>
                    Custom range
                </OptionButton>
            </div>

            {custom && (
                <div className="flex flex-col gap-3 rounded-[12px] border border-[var(--line-2)] bg-[var(--surface-2)] p-3.5">
                    <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_120px]">
                        <span>
                            <label className="text-[12px] text-[var(--muted)]">Start ({timeZoneLabel()})</label>
                            <input
                                type="datetime-local"
                                value={startLocal}
                                max={toLocalInputValue(new Date())}
                                onChange={(e) => setStartLocal(e.target.value)}
                                className="mt-1.5 min-h-[42px] w-full rounded-[10px] border border-[var(--line-2)] bg-[var(--surface)] px-3 text-[13.5px] text-[var(--text)] outline-none focus:border-[var(--accent)]"
                            />
                        </span>
                        <span>
                            <label className="text-[12px] text-[var(--muted)]">Length (min)</label>
                            <input
                                type="number"
                                min={1}
                                max={MAX_CUSTOM_MINUTES}
                                value={customMinutes}
                                onChange={(e) => setCustomMinutes(Number(e.target.value))}
                                className="mt-1.5 min-h-[42px] w-full rounded-[10px] border border-[var(--line-2)] bg-[var(--surface)] px-3 text-[13.5px] tabular-nums text-[var(--text)] outline-none focus:border-[var(--accent)]"
                            />
                        </span>
                    </div>
                    {rangeProblem ? (
                        <p className="text-[12px] text-[var(--crit)]">{rangeProblem}</p>
                    ) : (
                        range && (
                            <p className="text-[12px] text-[var(--muted)]">
                                {formatDateTime(range.start)} → {formatTimeOfDay(range.end)}
                            </p>
                        )
                    )}
                    {dvrRange.data?.start && (
                        <p className="text-[11.5px] text-[var(--faint)]">
                            This unit holds footage back to {formatDateTime(dvrRange.data.start)}.
                        </p>
                    )}
                </div>
            )}
            <label className="text-[12px] text-[var(--muted)]">Quality</label>
            <div className="flex gap-2">
                {QUALITIES.map((q) => (
                    <OptionButton key={q.label} selected={quality.label === q.label} onClick={() => setQuality(q)}>
                        {q.label}
                    </OptionButton>
                ))}
            </div>
            {error && <p className="text-[12.5px] text-[var(--crit)]">{error}</p>}
        </Modal>
    );
};
